import { useEffect, useMemo, useState } from "react";
import format from "date-fns/format";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "@idw-types/state/application-state";
import {
    getServiceAssertionPerformanceReferenceDataReduxRequest,
    getServiceAssertionPerformancesReduxRequest
} from "@redux-requests/service-assertion-performance";
import { ServiceAssertionPerformanceSearchForm } from "./service-assertion-performance-search-form/service-assertion-performance-search-form";
import { ServiceAssertionPerformanceTableContainer } from "./service-assertion-performance-table-container/service-assertion-performance-table-container";
import { FormattedServiceAssertionPerformance } from "@idw-types/data/reports/service-assertion-performance";
import { SERVER_DATE_TIME_FORMAT, SERVER_DATE_FORMAT } from "@constants/date-formats";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { ClaimStatusTypes } from "@enums/claim-status-types";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import parse from "date-fns/parse";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { ServiceAssertionPerformanceSearchFiltersState } from "@idw-types/state/service-assertion-performance-state";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { setServiceAssertionPerformanceSearchFilters } from "@redux/actions/service-assertion-performance-actions";
import { initialServiceAssertionPerformanceState } from "@redux/reducers/service-assertion-performance-reducer";

export const ServiceAssertionPerformanceContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const {
        referenceData,
        searchFilters,
        searchResults,
        serviceAssertionPerformanceTableState
    } = useSelector((state: ApplicationState) => ({
        referenceData: state.serviceAssertionPerformance.referenceData,
        searchFilters: state.serviceAssertionPerformance.searchFilters,
        searchResults: state.serviceAssertionPerformance.searchResults,
        serviceAssertionPerformanceTableState: state.tableSettings.serviceAssertionPerformance
    }));

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getServiceAssertionPerformanceReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.apAssertions &&
            referenceData.assertions &&
            referenceData.policyDecisions &&
            referenceData.serviceOfferings &&
            referenceData.serviceOfferingMap &&
            referenceData.serviceOfferings &&
            referenceData.services
        );
    }, [
        referenceData.apAssertions,
        referenceData.assertions,
        referenceData.policyDecisions,
        referenceData.serviceOfferings,
        referenceData.serviceOfferingMap,
        referenceData.serviceOfferings,
        referenceData.services
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading;
    }, [referenceData.isLoading]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: ServiceAssertionPerformanceSearchFiltersState = {
                rpServiceId: searchParams.get("rpServiceId") || initialServiceAssertionPerformanceState.searchFilters.rpServiceId,
                policyDecisions: searchParams.get("policyDecisions")?.split(",") || initialServiceAssertionPerformanceState.searchFilters.policyDecisions,
                version: searchParams.get("version") || initialServiceAssertionPerformanceState.searchFilters.version,
                serviceOfferingIds: searchParams.get("serviceOfferingIds")?.split(",") || initialServiceAssertionPerformanceState.searchFilters.serviceOfferingIds,
                timeRangeType: (searchParams.get("timeRangeType") || initialServiceAssertionPerformanceState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialServiceAssertionPerformanceState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialServiceAssertionPerformanceState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setServiceAssertionPerformanceSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = async (newSearchFilters: ServiceAssertionPerformanceSearchFiltersState) => {
        const { rpServiceId, policyDecisions, version, serviceOfferingIds, startDate, endDate } = newSearchFilters;

        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE));
        dispatch(setServiceAssertionPerformanceSearchFilters(newSearchFilters));

        getServiceAssertionPerformancesReduxRequest(dispatch)(
            rpServiceId,
            version,
            policyDecisions,
            serviceOfferingIds,
            startDate,
            endDate,
            referenceData.apAssertions,
            referenceData.assertions,
            referenceData.serviceOfferings
        );
    };

    const handleClickOnTableCellLink = (claimStatusType: ClaimStatusTypes, serviceAssertionPerformance: FormattedServiceAssertionPerformance) => {
        const queryParametersObject = {
            startDate: format(startOfDay(searchFilters.startDate), SERVER_DATE_TIME_FORMAT),
            endDate: format(endOfDay(searchFilters.endDate), SERVER_DATE_TIME_FORMAT),
            timeRangeType: TimeRangeTypes.CUSTOM,
            shouldSearchOnLoad: "true",
            claimStatuses: claimStatusType,
            serviceOfferingIds: "",
            assertionIds: ""
        };

        if (serviceAssertionPerformance.apAssertion?.serviceOffering?.id) {
            queryParametersObject.serviceOfferingIds = serviceAssertionPerformance.apAssertion.serviceOffering.id;
        } else {
            delete queryParametersObject.serviceOfferingIds
        }

        if (serviceAssertionPerformance.apAssertion?.assertion?.id) {
            queryParametersObject.assertionIds = serviceAssertionPerformance.apAssertion.assertion.id;
        } else {
            delete queryParametersObject.assertionIds;
        }

        const queryParameters = createSearchParams(queryParametersObject).toString();
        navigateToUtilWithQueryParameters(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname, queryParameters);
    };


    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.serviceAssertionPerformance.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <ServiceAssertionPerformanceSearchForm
                        requestInProgress={searchResults.isLoading}
                        policyDecisions={referenceData.policyDecisions}
                        services={referenceData.services}
                        serviceOfferings={referenceData.serviceOfferings}
                        serviceOfferingMap={referenceData.serviceOfferingMap}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.serviceAssertionPerformances && (
                    <ServiceAssertionPerformanceTableContainer
                        handleClickOnTableCellLink={handleClickOnTableCellLink}
                        serviceAssertionPerformances={searchResults.serviceAssertionPerformances}
                        passGreenRange={referenceData.passGreenRange}
                        passRedRange={referenceData.passRedRange}
                        passYellowRange={referenceData.passYellowRange}
                        failGreenRange={referenceData.failGreenRange}
                        failRedRange={referenceData.failRedRange}
                        failYellowRange={referenceData.failYellowRange}
                        unverifiedGreenRange={referenceData.unverifiedGreenRange}
                        unverifiedRedRange={referenceData.unverifiedRedRange}
                        unverifiedYellowRange={referenceData.unverifiedYellowRange}
                        tableState={serviceAssertionPerformanceTableState}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};